import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Engine } from "@babylonjs/core";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import * as Utils from "../../Utils";
import { saveToIndexedDB } from "../../services/indexedDb";
import PrintBox from "../PrintBox/PrintBox";
import "./CanvasRenderer.css";

const CanvasRenderer = ({
  print,
  palletID,
  storagetype,
  quad,
  showAdditionalLoadout,
  setShowAdditionalLoadout,
  refreshKey = 0,
}) => {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const [scene, setScene] = useState(null);
  const [canvasLevel, setCanvasLevel] = useState(0);
  const [maxCanvasLevel, setMaxCanvasLevel] = useState(0);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printResolver, setPrintResolver] = useState(null);
  const [printText, setPrintText] = useState("PRINT");
  const lastLoadOutNum = localStorage.getItem("lastLoadOut");

  // Initialize Babylon engine/scene and resizing
  useLayoutEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    let engine = null;
    let scene = null;
    let resizeObserver = null;

    const resizeCanvas = () => {
      if (!engine) return;

      const parent = canvas.parentElement;
      if (!parent) return;

      const rect = parent.getBoundingClientRect();

      canvas.width = Math.floor(rect.width);
      canvas.height = Math.floor(rect.height);

      engine.resize();
    };

    const init = () => {
      engine = new Engine(canvas, true, {
        adaptToDeviceRatio: true
      });
      engineRef.current = engine;

      scene = Utils.createScene(engine, storagetype);
      setScene(scene);

      engine.runRenderLoop(() => {
        scene.render();
      });

      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(resizeCanvas);
      });

      if (canvas.parentElement) {
        resizeObserver.observe(canvas.parentElement);
      }

      resizeCanvas();
    };

    requestAnimationFrame(init);

    window.addEventListener("keydown", Utils.handleKeyPress);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (engine) {
        engine.stopRenderLoop();
        engine.dispose();
      }

      engineRef.current = null;
      window.removeEventListener("keydown", Utils.handleKeyPress);
    };
  }, [storagetype]);

  // Show pallet whenever quad/palletID/storage changes
  useEffect(() => {
    if (scene) {
      Utils.clearBoxes(scene);
      console.log("lastLoadOut load scene")
      if (showAdditionalLoadout) {
        console.log("lastLoadOut load success")
        Utils.placeBoxesOnPalletHighlightAddons(
          scene,
          Utils.addBox,
          palletID,
          storagetype,
          quad,
          lastLoadOutNum
        );
      } else if (canvasLevel === 0) {
        Utils.placeBoxesOnPallet(scene, Utils.addBox, palletID, storagetype, quad);
      } else {
        Utils.placeBoxesOnPalletwithLevel(
          scene,
          Utils.addBox,
          palletID,
          storagetype,
          quad,
          canvasLevel
        );
      }
    }
  }, [scene, palletID, storagetype, quad, canvasLevel, showAdditionalLoadout, lastLoadOutNum, refreshKey]);


  useEffect(() => {
    if (!palletID || !storagetype || !quad) return;

    async function updateMaxLevel() {
      const maxLevel = await Utils.returnPlaceBoxesMaxLayer(storagetype, palletID, quad);
      setMaxCanvasLevel(maxLevel);
      setCanvasLevel(0); // reset to show full view initially
    }

    updateMaxLevel();
  }, [palletID, storagetype, quad, refreshKey]);

  const clickRight = async () => {
    if (!scene) return;
    Utils.clearBoxes(scene);
    setCanvasLevel((prev) => (prev >= 1 ? prev + 1 : 1));
    setShowAdditionalLoadout(false);
  };

  const clickLeft = async () => {
    if (!scene) return;
    Utils.clearBoxes(scene);
    setCanvasLevel((prev) => (prev > 0 ? prev - 1 : maxCanvasLevel));
    setShowAdditionalLoadout(false);
  };

  const handleReset = () => {
    setCanvasLevel(0);
    setShowAdditionalLoadout(false);
  };

  const requestPrintOrientation = () => {
    return new Promise((resolve) => {
      setPrintResolver(() => resolve);
      setPrintModalOpen(true);
    });
  };

  const handlePrint = async () => {
    const orientation = await requestPrintOrientation();
    if (!scene) return;

    setShowAdditionalLoadout(false);
    setPrintText("LOADING");

    try {
      const imagesObject = await Utils.saveCurrentCanvas(
        canvasRef.current,
        scene,
        palletID,
        storagetype,
        quad
      );

      // Safely extract arrays or default to empty arrays if imagesObject is undefined
      const imagesArray = imagesObject?.images || [];
      const overheadImagesArray = imagesObject?.overheadImages || [];

      // Save arrays safely to IndexedDB
      await saveToIndexedDB("imagesArray", imagesArray);
      await saveToIndexedDB("overheadImagesArray", overheadImagesArray);

      const data = encodeURIComponent(`${storagetype}:${palletID}:${quad}`);

      // Make sure these hash paths match your App router definitions!
      if (orientation === "portrait") {
        window.location.href = `#/portraitPrintout?data=${data}`;
      } else {
        // If your router uses NewLandscapePrintout, update the hash here:
        window.location.href = `#/landscapePrintout?data=${data}`;
      }
    } catch (error) {
      console.error("Error during print preparation:", error);
      // Even if canvas saving fails, safely save empty arrays so printout pages load empty views gracefully
      await saveToIndexedDB("imagesArray", []);
      await saveToIndexedDB("overheadImagesArray", []);

      const data = encodeURIComponent(`${storagetype}:${palletID}:${quad}`);
      window.location.href = orientation === "portrait"
        ? `#/portraitPrintout?data=${data}`
        : `#/landscapePrintout?data=${data}`;
    } finally {
      setPrintText("PRINT");
    }
  };

  return (
    <div className="canvas-renderer-container">
      <div className="canvas-wrapper">
        <button
          className="nav-button left"
          onClick={clickLeft}
          disabled={canvasLevel === 0}
          style={{ opacity: canvasLevel === 0 ? 0.5 : 1 }}
        >
          <NavigateBeforeIcon />
        </button>

        <button
          className="nav-button right"
          onClick={clickRight}
          disabled={canvasLevel >= maxCanvasLevel}
          style={{ opacity: canvasLevel >= maxCanvasLevel ? 0.5 : 1 }}
        >
          <NavigateNextIcon />
        </button>

        <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
      </div>

      <div className="canvasButton-banner">
        <button className="canvasButton" onClick={handleReset}>
          RESET
        </button>
        {print && <button className="canvasButton" onClick={handlePrint}>
          {printText}
        </button>}
        <div className="canvasButton" style={{ padding: "1px 6px" }}>
          LEVEL: {canvasLevel === 0 ? "OVERVIEW" : canvasLevel}
        </div>
      </div>
      <PrintBox
        open={printModalOpen}
        onClose={() => {
          setPrintModalOpen(false);
          setPrintResolver(null);
        }}
        onSubmit={(orientation) => {
          if (printResolver) {
            printResolver(orientation);
          }
          setPrintModalOpen(false);
          setPrintResolver(null);
        }}
      />
    </div>
  );
};

export default CanvasRenderer;
